// Form state, validation and request body shared by the LLM settings page and the
// Phishbox LLM tab. Field names match the backend llm_settings / phishbox custom_llm.
import { DEFAULT_AZURE_API_VERSION, getProvider } from "./llmProviders";

export const INITIAL_LLM_CONFIGS = {
  active: false,
  provider: "openai",
  base_url: getProvider("openai").defaultBaseUrl,
  api_key: "",
  auth_header: "Authorization",
  model: "",
  azure_deployment: "",
  azure_api_version: "",
  temperature: "",
  max_tokens: "",
  timeout: "400",
  verify_ssl: true,
};

const toInputValue = (value) => (value === null || value === undefined ? "" : String(value));

// Backend data -> form state (numbers become strings for the inputs). The api key is
// never sent back, so the form always starts with an empty key.
export const llmConfigsFromDB = (data) => {
  if (!data || !data.provider) return INITIAL_LLM_CONFIGS;
  return {
    ...INITIAL_LLM_CONFIGS,
    active: !!data.active,
    provider: data.provider,
    base_url: data.base_url || "",
    auth_header: data.auth_header || "Authorization",
    model: data.model || "",
    azure_deployment: data.azure_deployment || "",
    azure_api_version: data.azure_api_version || "",
    temperature: toInputValue(data.temperature),
    max_tokens: toInputValue(data.max_tokens),
    timeout: toInputValue(data.timeout ?? 400),
    verify_ssl: data.verify_ssl !== false,
  };
};

// Field changes when the provider is switched. Going back to the saved provider restores
// its URL and model; the typed key is always cleared.
export const providerChangeConfigs = (providerKey, current, saved) => {
  const next = getProvider(providerKey);
  return {
    provider: next.key,
    base_url: next.key === saved.provider ? saved.base_url : next.defaultBaseUrl,
    model: next.key === saved.provider ? saved.model : "",
    api_key: "",
    azure_api_version:
      next.key === "azure" ? current.azure_api_version || DEFAULT_AZURE_API_VERSION : "",
  };
};

const isValidUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// Returns {field: message}. Connection fields only have to be complete when requireAll.
export const validateLlmConfigs = (current, { requireAll, hasStoredKey }) => {
  const errors = {};
  const provider = getProvider(current.provider);

  if (requireAll) {
    if (!isValidUrl(current.base_url.trim())) {
      errors.base_url = "Enter a valid http(s) base URL.";
    }
    if (current.provider === "azure" && !current.azure_deployment.trim()) {
      errors.azure_deployment = "Deployment name is required for Azure OpenAI.";
    }
    if (provider.keyRequired && !current.api_key.trim() && !hasStoredKey) {
      errors.api_key = "API key is required.";
    }
  }

  const temperature = String(current.temperature).trim();
  if (
    temperature &&
    (isNaN(Number(temperature)) || Number(temperature) < 0 || Number(temperature) > 2)
  ) {
    errors.temperature = "Temperature must be between 0 and 2.";
  }
  const maxTokens = String(current.max_tokens).trim();
  if (maxTokens && (!Number.isInteger(Number(maxTokens)) || Number(maxTokens) < 1)) {
    errors.max_tokens = "Max tokens must be a positive integer.";
  }
  const timeout = String(current.timeout).trim();
  if (
    timeout &&
    (!Number.isInteger(Number(timeout)) || Number(timeout) < 5 || Number(timeout) > 1800)
  ) {
    errors.timeout = "Timeout must be between 5 and 1800 seconds.";
  }

  return errors;
};

export const buildLlmRequestBody = (current) => ({
  ...current,
  base_url: current.base_url.trim(),
  api_key: current.api_key.trim(),
  model: current.model.trim(),
  auth_header: current.auth_header.trim() || "Authorization",
  azure_deployment: current.azure_deployment.trim(),
  azure_api_version: current.azure_api_version.trim(),
});
